import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { clientApi, RetailerLabelType, RetailerManifest } from '../../api/client';
import { EmptyState } from '../../components/EmptyState';

const LABEL_TYPE_BADGE_STYLES: Record<RetailerLabelType, string> = {
  shipping: 'bg-blue-100 text-blue-800',
  pallet: 'bg-amber-100 text-amber-800',
  carton: 'bg-emerald-100 text-emerald-800',
  box: 'bg-violet-100 text-violet-800',
  innerpack: 'bg-cyan-100 text-cyan-800',
  placard: 'bg-rose-100 text-rose-800',
  custom: 'bg-slate-100 text-slate-800',
};

export function RetailersPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['client', 'retailers'],
    queryFn: () => clientApi.getRetailers(),
  });

  const retailers = data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Retailers</h1>
        <p className="mt-1 text-sm text-gray-600">
          Browse retailer-specific label guidance and template coverage.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <h2 className="text-sm font-semibold text-red-900">Unable to load retailers</h2>
          <p className="mt-1 text-sm text-red-700">
            {error instanceof Error ? error.message : 'An unexpected error occurred.'}
          </p>
        </div>
      )}

      {isLoading ? (
        <RetailerCardSkeletonGrid />
      ) : retailers.length === 0 ? (
        <div className="card">
          <EmptyState
            icon="🏪"
            title="No retailers configured"
            description="Retailer manifests will appear here once they are published."
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {retailers.map((retailer) => (
            <RetailerCard key={retailer.retailerId} retailer={retailer} />
          ))}
        </div>
      )}
    </div>
  );
}

function RetailerCard({ retailer }: { retailer: RetailerManifest }) {
  const templateCount = retailer.templates?.length ?? retailer.activeTemplates.length;

  return (
    <Link
      to={`/retailers/${retailer.retailerId}`}
      className="card block h-full transition-all hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900">{retailer.displayName}</h2>
          <p className="mt-1 truncate font-mono text-sm text-gray-500">{retailer.retailerId}</p>
        </div>
        <span className="inline-flex shrink-0 items-center rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
          {templateCount} template{templateCount === 1 ? '' : 's'}
        </span>
      </div>

      <div className="mt-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Label Types</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {retailer.labelTypes.map((labelType) => (
            <span
              key={labelType}
              className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${LABEL_TYPE_BADGE_STYLES[labelType]}`}
            >
              {formatSlug(labelType)}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-gray-100 pt-4 text-sm">
        <span className="text-gray-500">Last reviewed</span>
        <span className="font-medium text-gray-700">{formatDate(retailer.lastReviewedAt)}</span>
      </div>
    </Link>
  );
}

function RetailerCardSkeletonGrid() {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="card animate-pulse space-y-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-2">
              <div className="h-5 w-32 rounded bg-gray-200" />
              <div className="h-4 w-24 rounded bg-gray-100" />
            </div>
            <div className="h-6 w-24 rounded-full bg-gray-100" />
          </div>
          <div className="space-y-3">
            <div className="h-3 w-20 rounded bg-gray-100" />
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: 3 }).map((__, badgeIndex) => (
                <div key={badgeIndex} className="h-6 w-20 rounded-full bg-gray-100" />
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between border-t border-gray-100 pt-4">
            <div className="h-4 w-20 rounded bg-gray-100" />
            <div className="h-4 w-24 rounded bg-gray-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function formatSlug(value: string): string {
  return value
    .split('-')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}
